// Filtry poziome wyświetlane nad listingiem produktów

import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SortDown } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { IProductsFilter } from 'api/types';
import { Button } from 'components/controls';

import styles from 'theme/pages/Products/components/FiltersHorizontal/FiltersHorizontal.module.scss';

interface IFilter {
  filter_id: string;
  filter_value: string;
}

interface IProps {
  categoryId?: number;
  queryFilters: IFilter[];
  filtersData?: IProductsFilter[];
  onChange: (filters: IFilter[]) => void;
  wrap?: boolean;
}

const FiltersHorizontal: FC<IProps> = ({
  categoryId,
  onChange,
  queryFilters,
  filtersData,
  wrap = false
}) => {
  const { t } = useTranslation();

  const [currentProductsFilters, setCurrentProductsFilters] = useState<IFilter[]>([]);
  const [columnSearchTerms, setColumnSearchTerms] = useState<Record<string, string>>({});
  const [filterSearch, setFilterSearch] = useState('');

  useEffect(() => {
    setCurrentProductsFilters(queryFilters);
  }, [queryFilters]);

  const getSelectedValuesForFilter = (filterId: string): string[] =>
    currentProductsFilters
      .filter((f) => f.filter_id === filterId)
      .map((f) => f.filter_value);

  const handleCheckboxChange = (filterId: string, value: string, checked: boolean) => {
    setCurrentProductsFilters((prev) => {
      if (checked) return [...prev, { filter_id: filterId, filter_value: value }];
      return prev.filter((f) => !(f.filter_id === filterId && f.filter_value === value));
    });
  };

  const handleSelectAll = (filterId: string, visibleValues: string[], allSelected: boolean) => {
    setCurrentProductsFilters((prev) => {
      const withoutThis = prev.filter((f) => f.filter_id !== filterId);
      if (allSelected) return withoutThis;
      return [
        ...withoutThis,
        ...visibleValues.map((value) => ({ filter_id: filterId, filter_value: value }))
      ];
    });
  };

  if (!filtersData?.length) return null;

  const visibleFilters = filtersData.filter((f) =>
    f.label.toLowerCase().includes(filterSearch.toLowerCase())
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Products-components-FiltersHorizontal'
      )}>
      <div className={styles.filterSearchWrapper}>
        <input
          className={styles.filterSearch}
          type="text"
          placeholder={t('Szukaj filtra...')}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
        />
      </div>
      <div className={classnames(styles.filtersRow, { [styles.filtersRowWrap]: wrap })}>
        {visibleFilters.map((filter) => {
          const searchTerm = columnSearchTerms[filter.id] || '';
          const visibleValues = filter.values.filter((v) =>
            v.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          const selectedValues = getSelectedValuesForFilter(filter.id);
          const allVisibleSelected =
            visibleValues.length > 0 &&
            visibleValues.every((v) => selectedValues.includes(v.value));

          return (
            <div className={styles.filterColumn} key={filter.id}>
              <div className={styles.columnHeader} title={filter.label}>
                {filter.label}
              </div>
              <input
                className={styles.columnSearch}
                type="text"
                placeholder={t('Wyszukaj tekst')}
                value={searchTerm}
                onChange={(e) =>
                  setColumnSearchTerms((prev) => ({ ...prev, [filter.id]: e.target.value }))
                }
              />
              <label className={styles.selectAll}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() =>
                    handleSelectAll(
                      filter.id,
                      visibleValues.map((v) => v.value),
                      allVisibleSelected
                    )
                  }
                />
                <Trans>ZAZNACZ WSZYSTKO</Trans>
              </label>
              <div className={styles.valuesList}>
                {visibleValues.map((val) => (
                  <label className={styles.valueItem} key={val.value}>
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(val.value)}
                      onChange={(e) =>
                        handleCheckboxChange(filter.id, val.value, e.target.checked)
                      }
                    />
                    {val.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.actions}>
        <Button color="secondary" onClick={() => onChange(currentProductsFilters)}>
          <Trans>Filtruj</Trans>
        </Button>
      </div>
    </div>
  );
};

export default FiltersHorizontal;
